<template>
  <div
    class="player-info"
    :class="[
      { 'is-active': isActive, 'is-ai': player.isAI, 'is-folded': player.isFolded, 'is-confirmed': player.isLayoutConfirmed, 'is-mobile': isMobile }
    ]"
  >
    <div
      class="player-avatar"
      :class="{ editable: editable }"
      :title="editable ? '点击修改昵称' : ''"
      @click.stop="onAvatarClick"
    >
      <ChatBubble :bubble="chatBubble" />
      <div class="avatar" :class="player.avatar">
        <span v-if="player.isAI">🤖</span>
        <span v-else>👤</span>
      </div>
      <div v-if="isActive" class="turn-indicator"></div>
    </div>
    <div class="player-details">
      <div class="player-title-line">
        <div class="player-name">{{ player.name }}</div>
        <span class="total-score" :class="{ negative: player.totalScore < 0 }">总分: {{ player.totalScore }}</span>
      </div>
      <div v-if="hasRoomRateScore" class="room-rate-line">
        积分:
        <span :class="{ 'positive': player.roomRateScore > 0 }">
          {{ player.roomRateScore > 0 ? '+' : '' }}{{ player.roomRateScore || 0 }}
        </span>
        / {{ player.totalRoomRateScore || 0 }}
      </div>
      <div class="player-round-line">
        <button class="current-score-line score-detail-trigger" type="button" @click="$emit('show-score-detail', player)">
          本局:
          <span class="current-score" :class="{ 'positive': player.score > 0, 'negative': player.score < 0 }">
            {{ player.score > 0 ? '+' : '' }}{{ player.score }}
          </span>
        </button>
        <div v-if="hasResultSummary" class="row-score-line">
          <span class="row-score" :class="resultSummary.rowClasses.front">头{{ resultSummary.rows.front }}</span>
          <span class="row-score" :class="resultSummary.rowClasses.middle">中{{ resultSummary.rows.middle }}</span>
          <span class="row-score" :class="resultSummary.rowClasses.back">尾{{ resultSummary.rows.back }}</span>
        </div>
      </div>
      <div
        v-if="hasResultSummary && (resultSummary.sweepsMade > 0 || resultSummary.timesBeaten > 0 || resultSummary.isHomeRun || resultSummary.isFolded || resultSummary.specialHand)"
        class="result-tag-line"
      >
        <span v-if="resultSummary.sweepsMade > 0" class="result-tag sweep">打枪x{{ resultSummary.sweepsMade }}</span>
        <span v-if="resultSummary.isHomeRun" class="result-tag home-run">全垒打</span>
        <span v-if="resultSummary.isFolded" class="result-tag folded">乌龙</span>
        <span v-if="resultSummary.timesBeaten > 0" class="result-tag beaten">被打x{{ resultSummary.timesBeaten }}</span>
        <span v-if="resultSummary.specialHand" class="result-tag special">
          {{ resultSummary.specialHand.name }}{{ resultSummary.specialScore ? formatSigned(resultSummary.specialScore) : '' }}
        </span>
      </div>
      <div v-if="player.specialHand && !hasResultSummary" class="special-hand">
        {{ player.specialHand.name }}
      </div>
      <div v-else-if="player.isFolded" class="folded-badge">
        乌龙
      </div>
      <div v-else-if="showConfirmedBadge && player.isLayoutConfirmed" class="confirmed-badge">
        已摆牌
      </div>
    </div>
  </div>
</template>

<script>
import ChatBubble from '@/components/ChatBubble.vue'

/**
 * 玩家信息面板。
 *
 * 显示内容分三层：
 * 1. 玩家名 + 总分；
 * 2. 本局分 + 头/中/尾三道净分；
 * 3. 打枪、被打、特殊牌型等标签。
 *
 * resultSummary 只用于展示本局摘要，真实结算仍来自 store/后端。
 */
export default {
  name: 'PlayerInfo',
  components: { ChatBubble },
  data() {
    return {
      isMobile: false
    }
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  },
  props: {
    player: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      default: false
    },
    showConfirmedBadge: {
      type: Boolean,
      default: true
    },
    resultSummary: {
      type: Object,
      default: null
    },
    editable: {
      type: Boolean,
      default: false
    },
    chatBubble: {
      type: Object,
      default: null
    }
  },
  computed: {
    // 父组件只在比牌完成后传入 resultSummary，摆牌/发牌阶段不展示摘要，避免提前剧透。
    hasResultSummary() {
      return !!(this.resultSummary && this.resultSummary.rows && this.resultSummary.rowClasses)
    },
    hasRoomRateScore() {
      return (this.player.roomRateScore || 0) !== 0 || (this.player.totalRoomRateScore || 0) !== 0
    }
  },
  methods: {
    onAvatarClick() {
      if (this.editable) this.$emit('edit-nickname', this.player)
    },
    // 特殊牌型分需要显式展示正负号，和本局分的显示规则保持一致。
    formatSigned(value) {
      if (value > 0) return `+${value}`
      return `${value}`
    }
  }
}
</script>

<style lang="scss" scoped>
// 比牌/结算时用户文字可能与牌重叠，用描边+阴影保证可读
$text-outline:
  -1px -1px 0 rgba(0, 0, 0, 0.9),
  1px -1px 0 rgba(0, 0, 0, 0.9),
  -1px 1px 0 rgba(0, 0, 0, 0.9),
  1px 1px 0 rgba(0, 0, 0, 0.9),
  0 -1px 0 rgba(0, 0, 0, 0.9),
  0 1px 0 rgba(0, 0, 0, 0.9),
  -1px 0 0 rgba(0, 0, 0, 0.9),
  1px 0 0 rgba(0, 0, 0, 0.9),
  0 2px 4px rgba(0, 0, 0, 0.7);

.player-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0;
  transition: all 0.3s ease;

  width: 180px;

  &.is-active {
    .avatar {
      box-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
    }
  }

  &.is-ai {
    opacity: 0.9;
  }

  &.is-folded {
    opacity: 0.5;

    .avatar {
      filter: grayscale(100%);
    }
  }

  &.is-confirmed:not(.is-folded) {
    .avatar {
      box-shadow: 0 0 10px rgba(39, 174, 96, 0.5);
    }
  }
}

.player-avatar {
  position: relative;

  &.editable {
    cursor: pointer;

    .avatar:hover {
      box-shadow: 0 0 12px rgba(255, 215, 0, 0.7);
      transform: scale(1.05);
    }
  }
  
  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    
    &.ai-1 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
    &.ai-2 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
    &.ai-3 { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
    &.ai-4 { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
    &.player { background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%); }
  }
  
  .turn-indicator {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    background: #27ae60;
    border-radius: 50%;
    border: 2px solid #1a1a2e;
    animation: pulse 1.5s infinite;
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
}

.player-details {
  flex: 1;
}

.player-title-line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.player-name {
  color: white;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 2px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: $text-outline;
}

.total-score {
  color: rgba(0, 255, 0, 1);
  font-size: 12px;
  white-space: nowrap;
  text-shadow: $text-outline;

  &.negative {
    color: #e74c3c;
  }
}

.room-rate-line {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: rgba(255, 232, 133, 0.95);
  font-size: 11px;
  line-height: 1.1;
  white-space: nowrap;
  text-shadow: $text-outline;

  .positive {
    color: #ffd24a;
    font-weight: bold;
  }
}

.player-round-line {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  flex-wrap: wrap;
}

.current-score-line {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.28);
  color: rgba(255, 255, 255, 0.86);
  white-space: nowrap;
  text-shadow: $text-outline;
}

.score-detail-trigger {
  border: 0;
  font: inherit;
  cursor: pointer;
  outline: none;

  &:hover,
  &:focus-visible {
    background: rgba(255, 216, 92, 0.22);
    box-shadow: 0 0 0 1px rgba(255, 216, 92, 0.5);
  }
}
  
.current-score {
  font-weight: bold;
  
  &.positive { color: #27ae60; }
  &.negative { color: #e74c3c; }
}

.special-hand {
  margin-top: 4px;
  padding: 2px 6px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #1a1a2e;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  display: inline-block;
}

.row-score-line,
.result-tag-line {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 10px;
  line-height: 1.15;
}

.result-tag-line {
  margin-top: 2px;
}

.row-score {
  font-family: monospace;
  font-weight: bold;
  text-shadow: $text-outline;

  &.positive { color: #7fff7f; }
  &.negative { color: #ff7f7f; }
  &.zero { color: rgba(255, 255, 255, 0.62); }
}

.result-tag {
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: bold;
  white-space: nowrap;

  &.sweep {
    background: #e74c3c;
    color: white;
  }

  &.beaten {
    background: rgba(255, 255, 255, 0.2);
    color: #ffb3b3;
  }

  &.folded {
    background: #8e44ad;
    color: white;
  }

  &.home-run {
    background: linear-gradient(135deg, #ffe066, #ff7b00 55%, #ff2d55);
    color: #2b1300;
    box-shadow: 0 0 8px rgba(255, 200, 0, 0.45);
  }

  &.special {
    background: linear-gradient(135deg, #ffd700, #ff8c00);
    color: #1a1a2e;
  }
}

.folded-badge {
  margin-top: 4px;
  padding: 2px 6px;
  background: #e74c3c;
  color: white;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  display: inline-block;
}

.confirmed-badge {
  margin-top: 4px;
  padding: 2px 6px;
  background: #27ae60;
  color: white;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  display: inline-block;
}

// 移动端样式（通过 JS 类名触发）
.player-info.is-mobile {
  width: 80px;
  padding: 0;
  gap: 3px;

  .player-avatar .avatar {
    width: 24px;
    height: 24px;
    font-size: 12px;
    border-width: 1px;
  }

  .player-name {
    font-size: 10px;
    margin-bottom: 0;
  }

  .player-title-line {
    gap: 2px;
  }

  .player-round-line {
    font-size: 11px;
    gap: 1px;
  }

  .total-score {
    font-size: 9px;
  }

  .current-score-line {
    padding: 2px 4px;
    border-radius: 2px;
  }

  .current-score {
    font-size: 13px;
  }

  .turn-indicator {
    width: 8px;
    height: 8px;
    bottom: -2px;
    right: -2px;
    border-width: 1px;
  }

  .special-hand,
  .folded-badge,
  .confirmed-badge {
    font-size: 8px;
    padding: 1px 2px;
    margin-top: 1px;
    border-radius: 2px;
  }

  .row-score-line,
  .result-tag-line {
    gap: 2px;
  }

  .row-score-line {
    font-size: 11px;
  }

  .result-tag-line {
    font-size: 8px;
  }

  .row-score {
    font-size: 11px;
  }

  .result-tag {
    max-width: 100%;
    padding: 1px 2px;
    font-size: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
